import { trpc } from '@/utils/trpc';
import { captureException } from '@sentry/nextjs';
import clsx from 'clsx';
import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';
import useEvent from '../../hooks/useEvent';
import { TCollection } from '../../interfaces/collections';

import EditMoodBoard from '../moodboard/EditMoodBoard';
import MediaViewer from './MediaViewer';
import ModalContainer from './ModalContainer';
import useNotification from '@/hooks/useNotification';

interface BookmarkButtonProps {
  media: any;
  size?: string;
  onClick?: (value: boolean) => void;
  shouldShow?: boolean;
}

const BookmarkButton = ({
  media,
  size = 'large',
  onClick,
  shouldShow = true,
}: BookmarkButtonProps) => {
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState<{
    type: string;
    active: boolean;
    collection: TCollection;
  }>();
  const [activeCollections, setActiveCollections] = useState<number[]>([]);
  const { data: collectionData } = trpc.collections.getUserCollections.useQuery(undefined, {
    staleTime: Infinity,
  });

  const [showNewBoardModal, setShowNewBoardModal] = useState(false);

  const elRef = useRef<HTMLDivElement>(null);

  const handleButtonClick = (e) => {
    e.stopPropagation();
    setShowModal(!showModal);
    if (onClick) {
      onClick(!showModal);
    }
  };

  const handleCreateBoardClick = (e) => {
    handleButtonClick(e);
    setShowNewBoardModal(true);
  };

  useEffect(() => {
    // check if this media is in the user bookmarked list
    if (!collectionData?.collections || collectionData.collections.length < 1) return;
    let bookmarked = false;
    let foundInCollection: number[] = [];
    collectionData.collections.forEach((collection) => {
      collection.bookmarks.forEach((bookmark) => {
        if (bookmark.media_id === media.id && bookmark.type === media.type) {
          bookmarked = true;
          foundInCollection.push(Number(collection.id));
        }
      });
    });

    setIsBookmarked(bookmarked);
    setActiveCollections(foundInCollection);
  }, [media, collectionData?.collections]);

  const handleModalClick = (e) => {
    const el = elRef?.current;
    if (!el || el.contains(e.target)) {
      return;
    }
    setShowModal(false);
  };

  // close modal click listener
  useEffect(() => {
    if (showModal) {
      document.addEventListener('mousedown', handleModalClick);
    } else {
      document.removeEventListener('mousedown', handleModalClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleModalClick);
    };
  }, [showModal]);

  const saveNewBoard = (collection) => {
    setMessage({ type: 'add', active: true, collection });
    setTimeout(() => {
      setMessage({ type: 'add', active: false, collection });
    }, 2000);
    setShowNewBoardModal(false);
  };

  const fallback = { top: 0, bottom: 0, left: 0, right: 0 };
  const { top, bottom, left, right } = elRef?.current?.getBoundingClientRect() || fallback;
  const bottomPos = window.innerHeight - bottom;
  const rightPos = window.innerWidth - right;

  const popoutStyles = {
    top: top > bottomPos ? 'auto' : 'calc(100% + 8px)',
    bottom: bottomPos > top ? 'auto' : 'calc(100% + 8px)',
    right: left > rightPos ? '0' : 'auto',
    left: rightPos > left ? '0' : 'auto',
  };

  return (
    <>
      <div ref={elRef} className={clsx(['relative', shouldShow ? 'opacity-100' : 'opacity-0'])}>
        <div className="grid justify-end" onClick={(e) => handleButtonClick(e)}>
          <BookmarkPlusButton size={size} isBookmarked={isBookmarked}></BookmarkPlusButton>
        </div>

        <div
          onClick={(e) => e.stopPropagation()}
          className={clsx([
            'bg-black border z-40 border-white absolute text-white w-[475px] max-h-[435px] grid grid-rows-[min-content_1fr_min-content]',
            showModal ? '' : 'hidden',
          ])}
          style={popoutStyles}
        >
          <div className="grid grid-cols-[1fr_min-content] p-[32px]">
            <div className="text-[14px] uppercase font-normal font-adieu leading-[1.4] tracking-[0.15em] text-white">
              Choose a Board
            </div>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={(e) => handleButtonClick(e)}
            >
              <path
                d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z"
                fill="white"
              />
            </svg>
          </div>
          {collectionData?.collections && collectionData?.collections.length > 0 && (
            <ul className="gap-[16px] grid p-[32px] pt-0 h-full overflow-scroll hide-scrollbar">
              {collectionData?.collections.map((collection, index) => {
                const active =
                  activeCollections && activeCollections.includes(Number(collection.id));

                const findFirstPhoto = collection?.bookmarks.find(
                  (media) => media.type === 'photo'
                );
                const coverPhoto = findFirstPhoto?.photo?.imgix.links.small_image_url;

                return (
                  <BookmarkButtonBoardItem
                    collection={collection}
                    active={active}
                    showModal={showModal}
                    setShowModal={setShowModal}
                    onClick={onClick}
                    media={media}
                    coverPhoto={coverPhoto}
                    setMessage={setMessage}
                    key={Number(collection.id)}
                  />
                );
              })}
            </ul>
          )}
          <div
            className="cursor-pointer grid gap-[16px] grid-cols-[40px_1fr] items-center p-[32px]"
            onClick={(e) => handleCreateBoardClick(e)}
          >
            <svg
              className="w-[40px] h-[40px] top-0 left-0"
              preserveAspectRatio="none"
              width="40"
              height="40"
              viewBox="0 0 41 41"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19.3434 20.376V23.864H20.6714V20.376H24.1594V19.048H20.6554V15.544H19.3274V19.048H15.8394V20.376H19.3434Z"
                fill="white"
              />
              <rect
                x="0.5"
                y="0.5"
                width="40"
                height="40"
                rx="8"
                stroke="white"
                vectorEffect="non-scaling-stroke"
                strokeDasharray="6 6"
              />
            </svg>

            <div className="text-[14px] uppercase font-normal font-adieu leading-[1.4] tracking-[0.15em] text-white">
              Create a Board
            </div>
          </div>
        </div>
      </div>
      {message?.collection?.id && (
        <Link href={`/collection/${message?.collection?.uuid}`}>
          <div className="contents" onClick={(e) => e.stopPropagation()}>
            <div
              className={clsx([
                'fixed bottom-[30px] z-40',
                message?.active ? 'translate-y-0' : 'translate-y-[calc(100% + 30px)] opacity-0',
                'left-[50%] transition-transform duration-500 translate-x-[-50%]',
                message?.type === 'add' ? 'bg-white' : 'bg-black',
                'grid grid-cols-[33px_max-content] gap-[20px] p-[12px] rounded-[10px]',
                media.type === 'photo'
                  ? 'grid-cols-[33px_max-content]'
                  : 'grid-cols-[0px_max-content]',
              ])}
            >
              {media && (
                <div className="flex place-items-center w-[33px] h-[33px] overflow-hidden">
                  {media.type === 'photo' && (
                    <MediaViewer
                      media={media}
                      showBookmarkButton={false}
                      showQuickDownload={false}
                      showVideoRuntime={false}
                      videoAutoPlay={true}
                      allowSingleModalView={false}
                      cover={true}
                    />
                  )}
                </div>
              )}
              <p
                className={clsx([
                  'text-[16px] pr-[12px] flex items-center',
                  message?.type === 'add' ? 'text-black' : 'text-white',
                ])}
              >
                {message?.type === 'add' ? 'Added to' : 'Removed from'}
                <b className="ml-[4px] font-medium">{' ' + message?.collection?.name}</b>.
              </p>
            </div>
          </div>
        </Link>
      )}

      {/* Modal for adding a new board with a single photo */}
      {showNewBoardModal && (
        <ModalContainer onClose={() => setShowNewBoardModal(false)}>
          <EditMoodBoard
            newBoard={true}
            onSaved={(res) => saveNewBoard(res)}
            onClose={() => {
              setShowNewBoardModal(false);
            }}
            media={media}
          />
        </ModalContainer>
      )}
    </>
  );
};

export default BookmarkButton;

export const BookmarkPlusButton = ({ isBookmarked, size = 'large' }) => {
  const [hover, setHover] = useState(false);
  return (
    <svg
      className="cursor-pointer fill-white stroke-black rounded-full hover:fill-orange-400 hover:stroke-white group"
      width={size === 'small' ? '24' : '32'}
      height={size === 'small' ? '24' : '32'}
      viewBox="0 0 32 32"
      xmlns="http://www.w3.org/2000/svg"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <path
        d="M32 16C32 24.8366 24.8366 32 16 32C7.16344 32 0 24.8366 0 16C0 7.16344 7.16344 0 16 0C24.8366 0 32 7.16344 32 16Z"
        stroke="none"
      />
      <path
        d="M16.7726 18.7028L16.5 18.5255L16.2274 18.7028L9.5 23.0783V8.5H23.5V23.0783L16.7726 18.7028Z"
        fill={isBookmarked ? (hover ? 'white' : 'black') : 'none'}
      />
    </svg>
  );
};

const BookmarkButtonBoardItem = ({
  collection,
  active,
  showModal,
  media,
  setShowModal,
  onClick,
  coverPhoto,
  setMessage,
}: {
  collection: TCollection;
  active: boolean;
  showModal: boolean;
  media: any;
  setShowModal: Function;
  onClick?: Function;
  coverPhoto?: string | null;
  setMessage: Function;
}) => {
  const mediaType = useMemo(() => {
    if (media.type === 'video') {
      return 'video';
    } else if (media.type === 'clip') {
      return 'clip';
    } else {
      return 'photo';
    }
  }, [media]);

  const { capture } = useEvent();

  const { refetch: refetchUserCollections } = trpc.collections.getUserCollections.useQuery(
    undefined,
    { staleTime: Infinity }
  );
  const { refetch: refetchCollections } = trpc.collections.getUserCollectionById.useQuery(
    String(collection.uuid),
    { staleTime: Infinity, enabled: !!collection?.uuid }
  );

  const toggleMediaToCollectionMutation = trpc.collections.toggleMediaToCollection.useMutation();
  const { showNotification } = useNotification();

  const addToCollection = async (collection) => {
    try {
      await toggleMediaToCollectionMutation.mutateAsync({
        type: mediaType,
        mediaId: Number(media.id),
        collectionId: Number(collection.id),
      });

      refetchUserCollections();
      refetchCollections();

      //capture bookmark event
      const eventName = `Bookmarked ${mediaType}`;
      const photo_id = mediaType === 'photo' ? media.id : undefined;
      const pack_id = mediaType === 'video' ? media.id : media.pack_id;
      const clip_id = mediaType === 'clip' ? media.id : undefined;

      capture({
        event: eventName,
        photo_id: photo_id,
        pack_id: pack_id,
        clip_id: clip_id,
      });

      setShowModal(!showModal);

      setMessage({ type: 'add', active: true, collection });
      setTimeout(() => {
        setMessage({ type: 'add', active: false, collection });
        setTimeout(() => {
          if (onClick) {
            onClick(!showModal);
          }
        }, 500);
      }, 2000);
    } catch (error) {
      showNotification({ message: `Unable to save board: ${error.message}` }, 6000);
      captureException(error);
    }
  };

  const removeFromCollection = async (collection) => {
    try {
      await toggleMediaToCollectionMutation.mutateAsync({
        type: mediaType,
        mediaId: Number(media.id),
        collectionId: Number(collection.id),
      });

      refetchUserCollections();
      refetchCollections();

      setShowModal(!showModal);
      setMessage({ type: 'remove', active: true, collection });
      setTimeout(() => {
        setMessage({ type: 'remove', active: false, collection });
        setTimeout(() => {
          if (onClick) {
            onClick(!showModal);
          }
        }, 500);
      }, 2000);
    } catch (error) {
      captureException(error);
    }
  };

  return (
    <li
      onClick={() => (active ? removeFromCollection(collection) : addToCollection(collection))}
      className="grid gap-[16px] grid-cols-[40px_1fr_min-content] items-center cursor-pointer group"
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      {coverPhoto ? (
        <img src={coverPhoto} alt="" className="w-[40px] h-[40px] object-cover" />
      ) : (
        <span className="w-[40px] h-[40px] block border border-white" />
      )}

      <div
        className={clsx([
          'text-[14px] uppercase font-normal font-adieu leading-[1.4] tracking-[0.15em]',
          active ? 'text-orange-400' : 'text-white',
        ])}
      >
        {collection.name}
      </div>
      <svg
        className={clsx(['group-hover:hidden', active ? 'opacity-100' : 'opacity-0'])}
        width="15"
        height="12"
        viewBox="0 0 15 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M5.125 8.80113L1.82375 5.49988L0.699585 6.61613L5.125 11.0415L14.625 1.54154L13.5088 0.425293L5.125 8.80113Z"
          fill="#FF3E00"
        />
      </svg>
      <p className="font-nhg font-normal uppercase leading-[1] tracking-[0.15em] relative text-sm 3xl:text-[15px] 4xl:text-[16px] text-orange-400 !hidden group-hover:!flex">
        {active ? 'Remove' : 'Save'}
      </p>
    </li>
  );
};
